import { createAnnouncement, createAuditAnimal, createManagementRow, OxIdentificationResponse } from '@/query';
import toast from 'react-hot-toast';
import {
  SyncIdentificationData,
  SyncIdentificationDexieService,
  SyncIdentificationManagementData,
  SyncIdentificationManagementDataAuthorizedDeletion
} from './database-service';

const databaseService = new SyncIdentificationDexieService();

interface DataSyncConfig {
  onProcessingChange: (isProcessing: boolean) => void;
  batchSize: number;
  intervalMs: number;
  maxRetries?: number;
}

export class DataSyncService {
  private intervalId: NodeJS.Timeout | null = null;
  private otherIntervalId: NodeJS.Timeout | null = null;
  private config: DataSyncConfig;
  public isProcessing = false;
  public pendingData = 0;

  constructor(config: DataSyncConfig) {
    this.config = { maxRetries: 3, ...config };
  }

  startBackgroundProcessing(callback: (data: SyncIdentificationManagementData[]) => Promise<void>): void {
    if (this.intervalId) return;

    this.intervalId = setInterval(() => {
      this.processQueue(callback, this.config.batchSize, false);
    }, this.config.intervalMs);

    this.processQueue(callback, this.config.batchSize, false);
  }

  async syncOtherData(callback: (data: SyncIdentificationManagementData[]) => void): Promise<void> {
    if (this.otherIntervalId) return;

    this.otherIntervalId = setInterval(async () => {
      const allData = await databaseService.getAllData();
      const data = allData.filter((item) => item.status === 'other');

      if (data.length > 0 && data.length !== this.pendingData) {
        this.pendingData = data.length;
        callback(data);
      }
    }, this.config.intervalMs);
  }

  stopBackgroundProcessing(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.otherIntervalId) {
      clearInterval(this.otherIntervalId);
      this.otherIntervalId = null;
    }
  }

  async addAuthorizedDeletionData(data: SyncIdentificationData): Promise<void> {
    try {
      await databaseService.addAuthorizedDeletionData(data);
    } catch (error) {
      console.error('Erro ao salvar dados:', error);
      toast.error('Ocorreu um erro ao salvar os dados na memoria local');
    }
  }

  async addData(data: SyncIdentificationData): Promise<void> {
    try {
      await databaseService.addPendingData(data);
    } catch (error) {
      console.error('Erro ao salvar dados:', error);
      toast.error('Ocorreu um erro ao salvar os dados na memoria local');
    }
  }

  async addOthersData(data: SyncIdentificationData): Promise<void> {
    try {
      await databaseService.addOthersData(data);
    } catch (error) {
      toast.error('Ocorreu um erro ao salvar os dados na memoria local');
    }
  }

  async deleteData(id: number): Promise<void> {
    await databaseService.deleteData(id);
  }

  async getSuccessData(): Promise<SyncIdentificationManagementData[]> {
    try {
      const data = await databaseService.getSuccessData();

      return data;
    } catch (error) {
      toast.error('Ocorreu um erro ao salvar os dados na memoria local');
      throw error;
    }
  }

  async getAllData(callback: (data: SyncIdentificationManagementData[]) => Promise<void>) {
    try {
      const data = await databaseService.getAllData();

      return callback(data);
    } catch (error) {
      toast.error('Ocorreu um erro ao salvar os dados na memoria local');
      throw error;
    }
  }

  async getManagedData(managementId: string, callback: (data: SyncIdentificationManagementData[]) => Promise<void>) {
    try {
      const data = (await databaseService.getManagedData()).reverse();

      const dataFiltered = data.filter((item) => item.data.managementId === managementId);

      return callback(dataFiltered);
    } catch (error) {
      toast.error('Ocorreu um erro ao salvar os dados na memoria local');
      throw error;
    }
  }

  async syncAllData(): Promise<SyncIdentificationManagementData[]> {
    try {
      const data = await databaseService.getAllData();

      return data;
    } catch (error) {
      return [];
    }
  }

  async processQueue(
    callback: (data: SyncIdentificationManagementData[]) => Promise<void>,
    batchSize: number | null = null,
    sync: boolean = true,
    _: boolean = false
    // getAll: boolean = false
  ): Promise<void> {
    if (this.isProcessing) return;

    const pendingItems = await databaseService.getPendingData(batchSize ?? this.config.batchSize, false);
    const authorizedDeletionItems = await databaseService.getAuthorizedDeletionData();

    if (authorizedDeletionItems.length > 0) {
      const callbackItensWithSuccess: SyncIdentificationManagementDataAuthorizedDeletion[] = [];

      for await (const item of authorizedDeletionItems) {
        try {
          if (!item.data.sisbov || !item.id) {
            continue;
          }

          await databaseService.deleteAuthorizedData(item.data.sisbov, item.id);

          callbackItensWithSuccess.push(item);
        } catch (error) {
          console.error('Erro ao deletar item:', error);
        }
      }

      if (callbackItensWithSuccess.length > 0) {
        await callback(
          callbackItensWithSuccess.map((item) => ({
            id: 1,
            data: item.data,
            gtaId: item.data.gtaId,
            sisbov: item.data.sisbov!,
            electronic: item.data.electronic ?? null,
            oxId: Math.floor(Math.random() * 1000),
            retryCount: 0,
            status: 'success',
            step: 'delete',
            timestamp: item.timestamp
          }))
        );
      }
    }

    if (pendingItems.length === 0) return;

    this.isProcessing = true;

    const dataSuccess: SyncIdentificationManagementData[] = [];

    for await (const item of pendingItems) {
      try {
        await this.processItem(item, sync);

        dataSuccess.push(item);
      } catch (error) {
        console.error('Erro ao processar item:', error);
      }
    }

    if (dataSuccess.length > 0) {
      await callback(dataSuccess);
    }

    this.isProcessing = false;
  }

  async processItem(item: SyncIdentificationManagementData, sync: boolean = true) {
    if (!item.id) {
      throw new Error('Não foi possível identificar o animal');
    }

    try {
      // Atualiza o status para processamento
      // await databaseService.updateDataStatus(item.id, 'processing');

      // console.log({ item });

      // throw new Error('Não foi possível identificar o animal');

      // console.log({ item });

      // throw new Error('Não foi possível identificar o animal');

      // console.log({ item });

      // throw new Error('Não foi possível identificar o animal');

      if (sync) {
        // Tenta identificar o animal
        await this.tryOxIdentification(item);

        // // Altera as características do animal
        // await this.tryChangeCharacteristic(item, ox);

        // // Adiciona ao histórico
        // await this.tryAddToHistory(item, ox);

        // // Atualiza o status para concluído
        // await databaseService.updateDataStatus(item.id, 'success');
      }
    } catch (error: any) {
      console.error('Erro ao processar item:', error);

      // if (item.retryCount < (this.config.maxRetries || 3)) {
      //   // Incrementa contador de retry
      //   await databaseService.incrementRetryCount(item.id);
      // } else {
      //   // Marca como falha após esgotar tentativas
      // }
      await databaseService.updateDataStatus(item.id, 'failed', error?.message);

      throw error;
    }
  }

  async retryFailedItems(): Promise<void> {
    const failedItems = await databaseService.getFailedData();

    for (const item of failedItems) {
      if (item.id) {
        await databaseService.updateDataStatus(item.id, 'pending');
      }
    }
  }

  async tryOxIdentification(item: SyncIdentificationManagementData): Promise<void> {
    if (!item.id) {
      throw new Error('Não foi possível identificar o animal');
    }

    try {
      const step = await databaseService.getStep(item.id);

      if (step !== 'identification') {
        throw new Error('Não foi possível identificar o animal');
      }

      await createAuditAnimal({
        sisbov: item.data.sisbov?.toString() || '',
        electronic: item.data.electronic || '',
        bacthOrigin: item.data.origin,
        batchDestiny: item.data.batchId,
        gta: item.gtaId.toString() || '',
        metadata: JSON.stringify(item.data),
        oxId: item.oxId
      });

      await databaseService.deleteData(item.id);
    } catch (error) {
      console.error('Erro ao identificar animal:', error);
      throw error;
    }
  }

  async tryChangeCharacteristic(item: SyncIdentificationManagementData, ox: OxIdentificationResponse): Promise<void> {
    if (!item.id) {
      throw new Error('Não foi possível alterar as características do animal');
    }

    try {
      const data = item.data;
      const step = await databaseService.getStep(item.id);

      if (step !== 'change-characteristic') return;

      // Deve criar um comunicado em caso de mudança de raça da BND
      if (data.breedBND && data.breedBND !== ox.breed) {
        await createAnnouncement({
          type: 'CHANGE_OF_CHARACTERISTIC' as any,
          date: new Date().toISOString(),
          data: [
            {
              oldBreed: ox.breed,
              newBreed: data.breedBND,

              oxId: ox.id
            }
          ]
        });
      }

      await databaseService.updateDataStep(item.id, 'add-to-history');
    } catch (error) {
      console.error('Erro ao alterar as características do animal:', error);

      throw error;
    }
  }

  async tryAddToHistory(item: SyncIdentificationManagementData, ox: OxIdentificationResponse): Promise<void> {
    if (!item.id) {
      throw new Error('Não foi possível adicionar o animal ao histórico');
    }

    try {
      const step = await databaseService.getStep(item.id);

      if (step !== 'add-to-history') return;

      await createManagementRow({
        managementId: item.data.managementId,
        oxId: ox.id,
        corral: item.data.batchId.toString()
      });

      await databaseService.updateDataStep(item.id, 'delete');
    } catch (error) {
      console.error('Erro ao Não foi possível adicionar o animal ao histórico:', error);

      throw error;
    }
  }

  async deleteItem(id: number): Promise<void> {
    await databaseService.deleteData(id);
  }

  async updateSisbovStatus(sisbov: number, status: SyncIdentificationManagementData['status']): Promise<void> {
    await databaseService.updateSisbovStatus(sisbov, status);
  }

  async upsertSisbov(data: SyncIdentificationData): Promise<void> {
    if (data?.sisbov) {
      const existing = await databaseService.findSisbov(data.sisbov);

      if (existing) {
        await databaseService.updateSisbovOnConfirmation(data.sisbov, data.oxId);
      } else {
        await databaseService.addOthersData(data);
      }

      return;
    }

    // Sem SISBOV: usa o brinco eletrônico como chave alternativa de deduplicação
    await this.upsertElectronic(data);
  }

  async upsertElectronic(data: SyncIdentificationData): Promise<void> {
    if (!data?.electronic) return;

    const existing = await databaseService.findElectronic(data.electronic);

    if (existing) {
      await databaseService.updateElectronicOnConfirmation(data.electronic, data.oxId);
    } else {
      await databaseService.addOthersData(data);
    }
  }

  async deleteSisbov(sisbov: number, callback: () => void): Promise<void> {
    await databaseService.deleteSisbov(sisbov, callback);
  }

  async deleteRejectedOther(sisbov: number): Promise<void> {
    await databaseService.deleteOtherBySisbov(sisbov);
  }

  async deleteRejectedOtherByElectronic(electronic: string): Promise<void> {
    await databaseService.deleteOtherByElectronic(electronic);
  }
}
